/**
 * The close of escrow date (loan_applications.closing_date): what the loan
 * pipeline shows, how it sorts, and which loans it flags. The LOS stores the
 * date as MM/DD/YYYY; "2026-11-14" is read too, anything else is shown as it
 * is and never flagged.
 */

/** How many days ahead a date counts as due soon. */
export const DUE_SOON_DAYS = 7

const DAY_MS = 24 * 60 * 60 * 1000

/** "11/14/2026" or "2026-11-14" → { year, month, day }; anything else null. */
export function parseCloseOfEscrowDate(value) {
    const text = String(value || "").trim()
    let m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text)
    let year, month, day
    if (m) [year, month, day] = [+m[3], +m[1], +m[2]]
    else if ((m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text))) [year, month, day] = [+m[1], +m[2], +m[3]]
    else return null
    const d = new Date(Date.UTC(year, month - 1, day))
    if (d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) return null
    return { year, month, day }
}

/** Whole calendar days from today to the date: 0 is today, negative once it has passed. */
export function daysUntil(date, now = new Date()) {
    const due = Date.UTC(date.year, date.month - 1, date.day)
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
    return Math.round((due - today) / DAY_MS)
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`

/**
 * The flag beside a loan's close of escrow date, or null:
 *   { kind: "overdue", days: 3, label: "Overdue by 3 days" }
 *   { kind: "soon", days: 0, label: "Due today" }
 * A loan that is finished (funded, withdrawn, denied...) is never flagged,
 * nor is a date more than DUE_SOON_DAYS away.
 */
export function closeOfEscrowFlag(value, { finished = false, now = new Date() } = {}) {
    if (finished) return null
    const date = parseCloseOfEscrowDate(value)
    if (!date) return null
    const days = daysUntil(date, now)
    if (days < 0) return { kind: "overdue", days: -days, label: `Overdue by ${plural(-days, "day")}` }
    if (days > DUE_SOON_DAYS) return null
    if (days === 0) return { kind: "soon", days, label: "Due today" }
    if (days === 1) return { kind: "soon", days, label: "Due tomorrow" }
    return { kind: "soon", days, label: `Due in ${plural(days, "day")}` }
}

const sortKey = (value) => {
    const date = parseCloseOfEscrowDate(value)
    return date ? Date.UTC(date.year, date.month - 1, date.day) : null
}

/**
 * An antd column sorter (antd negates the result for "descend"): earliest
 * date first, and loans without a date last whichever way the column sorts.
 */
export function compareCloseOfEscrow(a, b, sortOrder) {
    const ka = sortKey(a)
    const kb = sortKey(b)
    if (ka === null && kb === null) return 0
    const missingLast = sortOrder === "descend" ? -1 : 1
    if (ka === null) return missingLast
    if (kb === null) return -missingLast
    return ka - kb
}

/** The loan pipeline's Closing filter options, in the order the menu lists them. */
export const CLOSING_FILTERS = [
    { value: "overdue", label: "Overdue" },
    { value: "within7", label: "Within 7 days" },
    { value: "within30", label: "Within 30 days" },
]

/**
 * Whether a loan shows under a Closing filter option, read the same way as
 * the column's tag: a finished loan, or one without a date this module can
 * read, matches no option. "overdue" is a date that has passed; "within7"
 * and "within30" run from today through 7 or 30 days ahead and leave out
 * overdue loans, which have their own option. No option matches every loan.
 */
export function matchesClosingFilter(filter, value, { finished = false, now = new Date() } = {}) {
    if (!filter) return true
    if (finished) return false
    const date = parseCloseOfEscrowDate(value)
    if (!date) return false
    const days = daysUntil(date, now)
    switch (filter) {
        case "overdue":
            return days < 0
        case "within7":
            return days >= 0 && days <= DUE_SOON_DAYS
        case "within30":
            return days >= 0 && days <= 30
        default:
            return true
    }
}
